// SolmanChangeHistory.jsx — riwayat dokumen baru / versi baru / dihapus yang terdeteksi saat Sync list
// (manual "Run pipeline" atau cron solman_docs_sync). Backend: GET /api/solman-docs/changes
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, History, Loader2, RefreshCw } from 'lucide-react';

async function api(url) {
  const r = await fetch(url, { headers: { Authorization: 'Bearer ' + (localStorage.getItem('auth_token') || '') } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || ('HTTP ' + r.status));
  return j;
}
// SAP "YYYYMMDDHHMMSS" → "YYYY-MM-DD HH:MM"
export const fmtSapTime = (t) => {
  const s = String(t || '');
  if (!/^\d{12,14}$/.test(s) || /^0+$/.test(s)) return '';
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
};
const fmtIso = (t) => {
  if (!t) return '';
  const d = new Date(t);
  if (isNaN(d)) return String(t);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const BADGE = {
  new: { label: 'New', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  updated: { label: 'New version', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  removed: { label: 'Removed', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export default function SolmanChangeHistory({ strucId, onShow }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [kind, setKind] = useState('');
  const [scope, setScope] = useState('all');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const qs = new URLSearchParams({ days: String(days), limit: '500' });
      if (kind) qs.set('change', kind);
      if (scope === 'project' && strucId) qs.set('strucId', strucId);
      setData(await api('/api/solman-docs/changes?' + qs));
    } catch (e) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }, [days, kind, scope, strucId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const rows = (data && data.rows) || [];
  const cnt = (k) => rows.filter((r) => r.change === k).length;

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm">
        <span className="flex items-center gap-2 font-medium text-slate-700">
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          <History size={15} className="text-indigo-600" /> Document change history
        </span>
        <span className="text-xs text-slate-500">New / updated / removed documents detected by Sync (manual or nightly schedule)</span>
      </button>
      {open && (
        <div className="px-3 pb-3">
          <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="border border-slate-300 rounded px-2 py-1">
              {[7, 30, 90, 365, 3650].map((d) => <option key={d} value={d}>{d === 3650 ? 'All time' : `Last ${d} days`}</option>)}
            </select>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="border border-slate-300 rounded px-2 py-1">
              <option value="">All changes</option><option value="new">New</option>
              <option value="updated">New version</option><option value="removed">Removed</option>
            </select>
            <select value={scope} onChange={(e) => setScope(e.target.value)} className="border border-slate-300 rounded px-2 py-1">
              <option value="all">All projects</option>
              <option value="project" disabled={!strucId}>Selected project only</option>
            </select>
            <button type="button" onClick={load} disabled={busy}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
            </button>
            {data && <span className="text-slate-500">{rows.length} change(s) · {cnt('new')} new · {cnt('updated')} new version · {cnt('removed')} removed</span>}
          </div>
          {err && <div className="text-xs text-rose-600 mb-2">{err}</div>}
          {data && !rows.length && <div className="text-xs text-slate-500 py-3">No document changes detected in this period.</div>}
          {!!rows.length && (
            <div className="max-h-[45vh] overflow-auto border border-slate-100 rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">Detected</th>
                    <th className="text-left px-2 py-1.5">Change</th>
                    <th className="text-left px-2 py-1.5">Document</th>
                    <th className="text-left px-2 py-1.5">Project › Group</th>
                    <th className="text-left px-2 py-1.5">Created / Updated in Solman</th>
                    <th className="text-left px-2 py-1.5">By</th>
                    <th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => {
                    const b = BADGE[r.change] || BADGE.new;
                    return (
                      <tr key={r.id} className="align-top">
                        <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">{fmtIso(r.detected_at)}</td>
                        <td className="px-2 py-1.5"><span className={`inline-block px-1.5 py-0.5 rounded border ${b.cls}`}>{b.label}</span></td>
                        <td className="px-2 py-1.5 text-slate-800">{r.descript}{r.doc_format ? <span className="text-slate-400"> · {r.doc_format}</span> : null}</td>
                        <td className="px-2 py-1.5 text-slate-500">{[r.struc_name, r.node_name].filter(Boolean).join(' › ')}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">
                          {fmtSapTime(r.crea_time)}{r.chng_time && r.chng_time !== r.crea_time ? <div className="text-slate-400">upd {fmtSapTime(r.chng_time)}</div> : null}
                        </td>
                        <td className="px-2 py-1.5 text-slate-500">{r.actor || ''}</td>
                        <td className="px-2 py-1.5">
                          {r.change !== 'removed' && onShow && (
                            <button type="button" onClick={() => onShow(r)}
                              className="px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Show</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
